import { useMemo, useState } from "react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check, X, ShieldCheck } from "lucide-react";
import * as Icons from "lucide-react";
import { getAllMicroservices, usdToEur, type Microservice, type MicroserviceCategory } from "@/content/microservices";
import { LocalizedLink } from "@/components/routing/LocalizedLink";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import paymentsCover from "@/assets/services/microservices/payments-cover.jpg";
import complianceCover from "@/assets/services/microservices/compliance-cover.jpg";
import salesCover from "@/assets/services/microservices/sales-cover.jpg";
import operationsCover from "@/assets/services/microservices/operations-cover.jpg";
import marketingCover from "@/assets/services/microservices/marketing-cover.jpg";
import financeCover from "@/assets/services/microservices/finance-cover.jpg";
import legalCover from "@/assets/services/microservices/legal-cover.jpg";

const CATEGORIES: readonly MicroserviceCategory[] = [
  "branding-design",
  "web-mobile",
  "marketing-acquisition",
  "content-seo",
  "video-creative",
  "product-ux",
  "data-ai",
  "ops-automation",
  "legal-compliance",
  "finance-fundraising",
  "sales-cs",
  "hr-talent",
  "payments-uemoa",
] as const;
type Category = MicroserviceCategory;

// Reuse existing artwork; map each new taxonomy slug to the closest legacy cover.
const COVERS: Record<Category, string> = {
  "branding-design": marketingCover,
  "web-mobile": operationsCover,
  "marketing-acquisition": marketingCover,
  "content-seo": marketingCover,
  "video-creative": marketingCover,
  "product-ux": operationsCover,
  "data-ai": operationsCover,
  "ops-automation": operationsCover,
  "legal-compliance": legalCover,
  "finance-fundraising": financeCover,
  "sales-cs": salesCover,
  "hr-talent": operationsCover,
  "payments-uemoa": paymentsCover,
};

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const C = (Icons as any)[name] ?? Icons.Box;
  return <C size={size} />;
}

export default function Microservices() {
  const { t, i18n } = useTranslation();
  const lang: "en" | "fr" = (i18n.language || "en").startsWith("fr") ? "fr" : "en";
  const [cat, setCat] = useState<Category | null>(null);
  const [active, setActive] = useState<Microservice | null>(null);
  const [q, setQ] = useState("");

  // Localize hardcoded "/mois" suffixes in content strings to the active locale.
  const monthSuffix = t("common.perMonthShort", { defaultValue: lang === "fr" ? "/mois" : "/mo" });
  const localizePrice = (s: string | undefined) =>
    (s ?? "").replace(/\/(mois|mo|mês|mes)\b/gi, monthSuffix);

  const all = useMemo(() => getAllMicroservices(), []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((m) => {
      if (cat && m.category !== cat) return false;
      if (!needle) return true;
      const hay = `${m.name.en} ${m.name.fr} ${m.tagline.en} ${m.tagline.fr}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [all, cat, q]);
  const countByCat = useMemo(() => {
    const c: Partial<Record<Category, number>> = {};
    for (const m of all) c[m.category] = (c[m.category] ?? 0) + 1;
    return c;
  }, [all]);

  return (
    <PublicShell>
      <Seo title={t("services.micro.seoTitle")} description={t("services.micro.subtitle")} />
      <section className="container py-10 md:py-14">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setCat(null)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              !cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {String(t("careers.chip.all", { defaultValue: lang === "fr" ? "Toutes" : "All" }))} ({all.length})
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition ${
                cat === c ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              {String(t(`services.micro.cat.${c}`, { defaultValue: c }))}{countByCat[c] ? ` (${countByCat[c]})` : ""}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "fr" ? "Rechercher un service…" : "Search services…"}
            className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <div className="mt-2 text-xs text-muted-foreground">
            {filtered.length} {lang === "fr" ? "résultats" : "results"}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {t("services.empty.results")}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((m) => (
              <button
                key={m.id}
                onClick={() => setActive(m)}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-all hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-lg"
              >
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={16 / 10}>
                    <img
                      src={COVERS[m.category]}
                      alt={m.name[lang]}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </AspectRatio>
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur">
                    <Icon name={m.icon} size={14} />
                    {String(t(`services.micro.cat.${m.category}`, { defaultValue: m.category }))}
                  </span>
                  {m.featured && (
                    <span
                      className="absolute right-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm"
                      style={{ background: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }}
                    >
                      {lang === "fr" ? "Populaire" : "Popular"}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <div className="text-base font-semibold text-foreground group-hover:text-primary">{m.name[lang]}</div>
                  <p className="line-clamp-2 text-sm text-muted-foreground">{m.tagline[lang]}</p>
                  {m.subscription_available && (
                    <span className="inline-flex w-fit items-center rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent">
                      {lang === "fr" ? "Abonnement" : "Subscription"}
                    </span>
                  )}
                  <div className="mt-auto flex items-end justify-between border-t border-border pt-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {lang === "fr" ? "À partir de" : "From"}
                      </div>
                      <div className="text-sm font-bold leading-tight text-foreground">
                        {m.tiers
                          ? `€${m.tiers.standard.price_eur?.min ?? usdToEur(m.tiers.standard.price_usd.min)}`
                          : localizePrice(m.pricing_eur || m.pricing_xof)}
                      </div>
                      <div className="text-[10px] leading-tight text-muted-foreground">
                        {m.tiers
                          ? (lang === "fr" ? "EUR · 3 paliers disponibles" : "EUR · 3 tiers available")
                          : localizePrice(m.pricing_eur)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                      {lang === "fr" ? "Voir" : "View"}
                      <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>{active?.name[lang]}</SheetTitle>
            </SheetHeader>
            {active && (
              <div className="mt-4 space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-foreground">
                    <Icon name={active.icon} size={12} />
                    {String(t(`services.micro.cat.${active.category}`, { defaultValue: active.category }))}
                  </span>
                  {active.subscription_available !== undefined && (
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${active.subscription_available ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>
                      {active.subscription_available
                        ? (lang === "fr" ? "Abonnement disponible" : "Subscription available")
                        : (lang === "fr" ? "À l'unité" : "One-off")}
                    </span>
                  )}
                </div>

                <div className="text-sm font-medium text-foreground">{active.tagline[lang]}</div>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{active.description[lang]}</p>

                {active.value_prop && active.value_prop[lang] && active.value_prop[lang] !== active.description[lang] && (
                  <div className="rounded-lg border border-border bg-muted/40 p-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {lang === "fr" ? "Proposition de valeur" : "Value proposition"}
                    </div>
                    <p className="mt-1 text-sm text-foreground">{active.value_prop[lang]}</p>
                  </div>
                )}

                {active.deliverable && active.deliverable[lang] && (
                  <div className="text-sm">
                    <span className="font-semibold text-foreground">
                      {lang === "fr" ? "Livrable : " : "Deliverable: "}
                    </span>
                    <span className="text-muted-foreground">{active.deliverable[lang]}</span>
                  </div>
                )}

                {active.tiers && (
                  <div className="text-sm">
                    <span className="font-semibold text-foreground">
                      {lang === "fr" ? "Délai : " : "Timeline: "}
                    </span>
                    <span className="text-muted-foreground">
                      {lang === "fr"
                        ? `Livré en ${active.tiers.standard.lead_days.min}–${active.tiers.standard.lead_days.max} jours`
                        : `Delivered in ${active.tiers.standard.lead_days.min}–${active.tiers.standard.lead_days.max} days`}
                    </span>
                  </div>
                )}

                {active.tiers ? (
                  <div className="grid gap-2 sm:grid-cols-3">
                    {(["basic", "standard", "premium"] as const).map((key) => {
                      const tier = active.tiers![key];
                      const label = key === "basic" ? (lang === "fr" ? "Essentiel" : "Basic") : key === "standard" ? "Standard" : "Premium";
                      const eur = tier.price_eur ?? { min: usdToEur(tier.price_usd.min), max: usdToEur(tier.price_usd.max) };
                      return (
                        <div key={key} className={`rounded-lg border p-3 ${key === "standard" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                          <div className="mt-1 font-display text-sm font-bold text-foreground">€{eur.min}–€{eur.max}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {lang === "fr" ? `${tier.lead_days.min}–${tier.lead_days.max} j` : `${tier.lead_days.min}–${tier.lead_days.max} days`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <div className="font-display text-2xl font-bold text-foreground">{localizePrice(active.pricing_xof)}</div>
                    <div className="text-xs text-muted-foreground">{localizePrice(active.pricing_eur)}</div>
                  </div>
                )}

                <Button asChild className="w-full">
                  <LocalizedLink to={`/services/microservices/${active.slug}`}>
                    {lang === "fr" ? "Voir la fiche complète" : "View full page"}
                  </LocalizedLink>
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  {lang === "fr"
                    ? "Les commandes seront ouvertes lorsque les prestataires partenaires seront en ligne."
                    : "Ordering opens once partner providers come online."}
                </p>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </section>

      {/* Comparison strip */}
      <section className="border-y border-border bg-muted/30">
        <div className="container py-12 md:py-16">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-primary">{t("services.micro.comparisonEyebrow")}</div>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground md:text-3xl">{t("services.micro.comparisonH")}</h2>
          </div>
          <div className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-sm font-bold text-muted-foreground">{t("services.micro.consulting")}</div>
              <ul className="mt-4 space-y-2">
                {(t("services.micro.consultingPoints", { returnObjects: true }) as string[]).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />{p}
                  </li>
                ))}
              </ul>
              <div className="mt-4 border-t border-border pt-4 text-sm font-bold text-foreground">{t("services.micro.consultingTotal")}</div>
            </div>
            <div className="rounded-2xl border-2 border-primary bg-primary/5 p-6">
              <div className="text-sm font-bold text-primary">{t("services.micro.ours")}</div>
              <ul className="mt-4 space-y-2">
                {(t("services.micro.oursPoints", { returnObjects: true }) as string[]).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />{p}
                  </li>
                ))}
              </ul>
              <div className="mt-4 border-t border-primary/20 pt-4 text-sm font-bold text-foreground">{t("services.micro.oursTotal")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container py-12 md:py-16">
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-primary">{t("services.micro.howEyebrow")}</div>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground md:text-3xl">{t("services.micro.howH")}</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="rounded-2xl border border-border bg-card p-5">
              <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 font-mono text-sm font-bold text-primary">0{n}</div>
              <h3 className="mt-3 text-base font-bold text-foreground">{t(`services.micro.step${n}Title`)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(`services.micro.step${n}Desc`)}</p>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-8 flex max-w-2xl items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
          <div>
            <div className="text-sm font-bold text-foreground">{t("services.micro.guaranteeLabel")}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t("services.micro.guaranteeText")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container py-12 md:py-16">
        <h2 className="mx-auto mb-6 max-w-3xl text-center font-display text-2xl font-bold text-foreground md:text-3xl">
          {t("services.micro.faqLabel")}
        </h2>
        <Accordion type="single" collapsible className="mx-auto max-w-3xl">
          {[1, 2, 3, 4].map((n) => (
            <AccordionItem key={n} value={`q${n}`}>
              <AccordionTrigger className="text-left text-sm font-semibold">{t(`services.micro.faq${n}Q`)}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">{t(`services.micro.faq${n}A`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="container py-12 text-center md:py-16">
          <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold text-foreground md:text-3xl">
            {t("services.micro.finalH")}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">{t("services.micro.finalSub")}</p>
        </div>
      </section>
    </PublicShell>
  );
}
